import { Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import LiningCodeSearch from "@/components/LiningCodeSearch";

/**
 * Jacket-side lining row — Body + Sleeve searchable inputs with a "🔗 Match all"
 * button that propagates the body lining value to sleeve (and optionally pant /
 * vest setters when wired by the parent so the cascade still works).
 *
 * Pant and Vest linings render in their own sections (see `SectionLiningRow`),
 * but the parent still passes their setters here so one click on "Match all"
 * syncs every section to the body lining color (matching-set order).
 *
 * Props:
 *   jacketStyling, setJacketStyling — read/write lining_color, sleeve_lining_color
 *   pantStyling,   setPantStyling   — optional, only used by "Match all" cascade
 *   vestStyling,   setVestStyling   — optional, only used by "Match all" cascade
 */
export default function LiningMatchRow({
  jacketStyling,
  setJacketStyling,
  pantStyling,
  setPantStyling,
  vestStyling,
  setVestStyling,
  testId = "lining-match",
}) {
  const body = jacketStyling?.lining_color || "";
  const sleeve = jacketStyling?.sleeve_lining_color || "";

  const hasCascadeTargets =
    Boolean(setPantStyling) || Boolean(setVestStyling);

  const matchAll = () => {
    if (!body) return;
    setJacketStyling?.({ ...jacketStyling, sleeve_lining_color: body });
    if (setPantStyling) {
      setPantStyling({ ...(pantStyling || {}), pant_lining_color: body });
    }
    if (setVestStyling) {
      setVestStyling({ ...(vestStyling || {}), vest_lining_color: body });
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-400">
          Lining Colors
        </p>
        <Button
          type="button"
          size="sm"
          onClick={matchAll}
          disabled={!body}
          className="h-6 text-[10px] px-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40"
          data-testid={`${testId}-match-all-btn`}
          title={
            hasCascadeTargets
              ? "Copy body lining to sleeve, pant & vest"
              : "Copy body lining to sleeve"
          }
        >
          <Link2 size={10} className="mr-1" /> Match all
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5 flex items-center gap-1">
            Body lining
            <code className="text-[9px] text-amber-400/80 font-mono">C-0714</code>
          </label>
          <LiningCodeSearch
            value={body}
            onChange={(v) =>
              setJacketStyling?.({ ...jacketStyling, lining_color: v })
            }
            placeholder="FLLDL031 / book code"
            testId={`${testId}-body-input`}
          />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 block mb-0.5 flex items-center gap-1">
            Sleeve lining
            <code className="text-[9px] text-amber-400/80 font-mono">C-0724</code>
          </label>
          <LiningCodeSearch
            value={sleeve}
            onChange={(v) =>
              setJacketStyling?.({ ...jacketStyling, sleeve_lining_color: v })
            }
            placeholder="Match fabric / book code"
            testId={`${testId}-sleeve-input`}
          />
        </div>
      </div>
    </div>
  );
}
